import { useField } from 'formik'
import React, { useState, useRef, useCallback } from 'react'
import { AutoComplete } from 'primereact/autocomplete'
import { useTranslation } from 'react-i18next'

import { fetchData } from '../helpers/fetchData'

const sharedPt = lang => ({
  input: {
    root: {
      className: 'input-field !opacity-100 !w-full'
    }
  },
  panel: { className: '!shadow-lg rounded-[8px] mt-2 pb-2 overflow-hidden' },
  item: { className: 'hover:text-primary capitalize !whitespace-normal !break-words !leading-snug' },
  list: { className: '!overflow-x-hidden' },
  loadingIcon: {
    className: `absolute top-[13px] ${lang === 'en' ? 'right-[12px]' : 'left-[12px]'
      }`
  },
  emptymesage: { className: '!text-center' }
})

const useAutoCompletePanelStyle = () => {
  const wrapperRef = useRef(null)
  const getPanelStyle = useCallback(() => {
    if (wrapperRef.current) {
      const w = wrapperRef.current.offsetWidth
      return { width: `${w}px` }
    }
    return {}
  }, [])
  return { wrapperRef, getPanelStyle }
}

const defaultOptionsFormat = response => {
  return response?.map(data => {
    return {
      label: data?.name,
      value: data?.id
    }
  })
}

const fetchOptions = async (
  query,
  fieldUrl,
  setSuggestions,
  optionsFormat,
  setIsLoading,
  requestMethod = 'POST'
) => {
  const searchValue = query?.query || query?.search

  if (!searchValue || searchValue.trim().length < 3) return
  setIsLoading(true)
  try {
    let payload = query
    const method = requestMethod.toUpperCase()
    const url =
      method === 'GET'
        ? `${fieldUrl}?${new URLSearchParams(payload).toString()}`
        : fieldUrl
    const response = await fetchData(
      method,
      url,
      method === 'GET' ? null : payload
    )
    if (response?.data) {
      let options = optionsFormat(response?.data)
      setSuggestions(options)
    } else {
      setSuggestions([])
    }
  } catch (e) {
    console.log(e)
  } finally {
    setIsLoading(false)
  }
}

const FormikAutoCompleteField = ({
  label,
  name,
  fieldUrl,
  className,
  parentClass = '',
  emptyMessage = 'No searched results found',
  optionsFormat = defaultOptionsFormat,
  queryKey = 'query',
  payload = {},
  requestMethod = 'POST',
  forceSelection = true,
  preserveValueOnClear = false,
  NavigationIcon,
  onIconClick = () => { },
  ...props
}) => {
  const [field, meta, helpers] = useField(name)
  const [suggestions, setSuggestions] = useState([])
  const [loading, setIsLoading] = useState(false)
  const [inputValue, setInputValue] = useState(null)
  const { i18n } = useTranslation()
  const { wrapperRef, getPanelStyle } = useAutoCompletePanelStyle()

  const handleComplete = event => {
    fetchOptions(
      { [queryKey]: event.query, ...payload },
      fieldUrl,
      setSuggestions,
      optionsFormat,
      setIsLoading,
      requestMethod
    )
  }

  return (
    <div className={`flex flex-col gap-1 w-full mb-1 ${parentClass}`}>
      <div className='flex items-center justify-between'>
        <label htmlFor={name} className='input-label'>
          {label}
        </label>
        {NavigationIcon && (
          <NavigationIcon
            size={17}
            className='text-primary cursor-pointer'
            onClick={onIconClick}
          />
        )}
      </div>
      <div ref={wrapperRef} className='w-full'>
        <AutoComplete
          loadingIcon={
            loading ? (
              <i
                className={`pi pi-spin pi-spinner text-primary absolute top-[9px] ${i18n.language === 'en' ? 'right-[12px]' : 'left-[12px]'
                  } `}
              />
            ) : (
              <span />
            )
          }
          id={name}
          field='label'
          {...field}
          {...props}
          value={
            preserveValueOnClear
              ? inputValue !== null
                ? inputValue
                : (Array.isArray(suggestions) &&
                    suggestions.find(option => option.value === field.value)) ||
                  field.value ||
                  null
              : field.value
          }
          suggestions={suggestions}
          completeMethod={handleComplete}
          forceSelection={forceSelection}
          onChange={event => {
            const value = event?.value

            if (!preserveValueOnClear) {
              helpers.setValue(value?.value || value || null)
            } else if (value && typeof value === 'object') {
              setInputValue(null)
              helpers.setValue(value.value ?? value)
            } else if (value === null || value === undefined) {
              setInputValue('')
            } else {
              setInputValue(value)
            }
          }}
          onClear={event => {
            if (!preserveValueOnClear) props.onClear?.(event)
          }}
          onBlur={() => {
            if (preserveValueOnClear) setInputValue(null)
            helpers.setTouched(true)
          }}
          pt={sharedPt(i18n.language)}
          panelStyle={getPanelStyle()}
          emptyMessage={emptyMessage}
          showEmptyMessage={true}
        />
      </div>
      {meta.error && meta.touched && (
        <p className='text-sm text-red-500'>{meta.error}</p>
      )}
    </div>
  )
}

const StandaloneAutoCompleteField = ({
  label,
  name,
  value = '',
  fieldUrl,
  className,
  parentClass = '',
  emptyMessage = 'No searched results found',
  optionsFormat = defaultOptionsFormat,
  queryKey = 'query',
  payload = {},
  requestMethod = 'POST',
  forceSelection = true,
  inputId,
  inputName,
  ...props
}) => {
  const [suggestions, setSuggestions] = useState([])
  const [loading, setIsLoading] = useState(false)
  const { i18n } = useTranslation()
  const { wrapperRef, getPanelStyle } = useAutoCompletePanelStyle()

  const handleComplete = event => {
    fetchOptions(
      { [queryKey]: event.query, ...payload },
      fieldUrl,
      setSuggestions,
      optionsFormat,
      setIsLoading,
      requestMethod
    )
  }

  return (
    <div className={`flex flex-col gap-1 w-full mb-1 ${parentClass}`}>
      {label && (
        <label htmlFor={name} className='input-label'>
          {label}
        </label>
      )}
      <div ref={wrapperRef} className='w-full'>
        <AutoComplete
          loadingIcon={
            loading ? (
              <i
                className={`pi pi-spin pi-spinner text-primary absolute top-[9px] ${i18n.language === 'en' ? 'right-[12px]' : 'left-[12px]'
                  }`}
              />
            ) : (
              <span />
            )
          }
          value={value}
          id={inputId}
          name={inputName}
          field='label'
          {...props}
          suggestions={suggestions}
          completeMethod={handleComplete}
          forceSelection={forceSelection}
          pt={sharedPt(i18n.language)}
          panelStyle={getPanelStyle()}
          emptyMessage={emptyMessage}
          showEmptyMessage={true}
        />
      </div>
    </div>
  )
}

// wrapper
const AutoCompleteField = props => {
  const { useFormik = true } = props

  if (useFormik) {
    return <FormikAutoCompleteField {...props} />
  }

  return <StandaloneAutoCompleteField {...props} />
}

export default AutoCompleteField
